import React, { forwardRef, ReactNode } from "react";

import { bem } from "@react-md/utils";
import { Button as AntDButton } from "antd";
// eslint-disable-next-line no-restricted-imports
import { ButtonProps as AntdButtonProps } from "antd/es/button";
import cn from "classnames";

import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

const block = bem("mmc-button");

export type ButtonType =
  | "ai"
  | "control"
  | "default"
  | "filled"
  | "link"
  | "pill"
  | "primary"
  | "text";

const mmcButtonTypeToAntdType: Record<ButtonType, AntdButtonProps["type"]> = {
  ai: "default",
  control: "link",
  default: "default",
  filled: "dashed",
  link: "link",
  pill: "text",
  primary: "primary",
  text: "text",
};

export interface ButtonProps extends Omit<AntdButtonProps, "label" | "type"> {
  children?: ReactNode;
  className?: string;
  iconOnly?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  type?: ButtonType;
}

/** MMC's themed wrapper around AntD's Button, forwarding a ref and translating MMC's ButtonType variants (ai, pill, filled, etc.) to AntD's type/shape props. */
const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      children,
      className,
      danger,
      iconOnly,
      leftIcon,
      rightIcon,
      shape,
      size = "middle",
      type = "default",
      ...antDButtonProps
    },
    ref
  ) => {
    return (
      <AntDButton
        className={cn(
          block({
            danger,
            ["icon-only"]: iconOnly,
            [size]: true,
            [type]: true,
          }),
          className
        )}
        ref={ref}
        {...antDButtonProps}
        danger={danger}
        // An explicit shape wins. Without one, `pill` keeps deriving `round` exactly as before,
        // so RoundButton and every existing pill call site are untouched.
        shape={shape ?? (type === "pill" ? "round" : "default")}
        size={size}
        type={mmcButtonTypeToAntdType[type]}
      >
        {leftIcon && <span className={block("icon")}>{leftIcon}</span>}
        {isNotEmpty(children) ? <span className={block("content")}>{children}</span> : null}
        {rightIcon && <span className={block("icon")}>{rightIcon}</span>}
      </AntDButton>
    );
  }
);

export default Button;
